import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "../lib/api";
import { AppShell, PageHeader } from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { money, fmtDateAU, DEMO_MONTH } from "../lib/format";
import { parseMoneyToCents } from "../lib/format";
import { toast } from "sonner";
import { Landmark, PiggyBank, Plus, Trash2, CheckCircle2 } from "lucide-react";

const TABS = [
  { key: "tax", label: "Tax", icon: Landmark, ring: "bg-indigo-50 text-indigo-600", bar: "bg-indigo-500" },
  { key: "super", label: "Super", icon: PiggyBank, ring: "bg-emerald-50 text-emerald-600", bar: "bg-emerald-500" },
];

export default function Reserves() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const ref = `${DEMO_MONTH.slice(0, 4)}-09-15`;
  const [tab, setTab] = useState("tax");
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(`${DEMO_MONTH}-15`);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const { data: summary, isLoading } = useQuery({
    queryKey: ["reserves-summary"],
    queryFn: async () => (await api.get(`/reserves/summary?ref=${ref}`)).data,
  });
  const { data: deposits } = useQuery({
    queryKey: ["reserves", tab],
    queryFn: async () => (await api.get(`/reserves?type=${tab}&ref=${ref}`)).data,
  });

  const meta = TABS.find((t) => t.key === tab);
  const s = summary?.[tab];
  const pct = s && s.suggested_cents > 0 ? Math.min(100, Math.round((s.reserved_cents / s.suggested_cents) * 100)) : (s?.reserved_cents > 0 ? 100 : 0);

  const openDialog = () => { setAmount(""); setNote(""); setDate(`${DEMO_MONTH}-15`); setOpen(true); };
  const save = async () => {
    if (parseMoneyToCents(amount) <= 0) return toast.error("Please enter an amount.");
    setSaving(true);
    try {
      await api.post("/reserves", { type: tab, date, amount_cents: parseMoneyToCents(amount), note: note || null });
      qc.invalidateQueries({ queryKey: ["reserves-summary"] });
      qc.invalidateQueries({ queryKey: ["reserves", tab] });
      setOpen(false);
      toast.success("Deposit logged");
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };
  const remove = async (id) => {
    try {
      await api.delete(`/reserves/${id}`);
      qc.invalidateQueries({ queryKey: ["reserves-summary"] });
      qc.invalidateQueries({ queryKey: ["reserves", tab] });
      toast.success("Deposit removed");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <AppShell>
      <PageHeader title="Tax & Super reserves" subtitle={summary?.financial_year?.label || "This financial year"} onBack={() => navigate("/more")} />
      <div className="px-5 pb-28 df-fade-in space-y-4">
        {/* Segmented control */}
        <div className="df-card p-1 grid grid-cols-2 gap-1" data-testid="reserve-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button key={t.key} data-testid={`reserve-tab-${t.key}`} onClick={() => setTab(t.key)}
                className={`h-11 rounded-xl flex items-center justify-center gap-2 text-sm font-semibold df-pressable transition-colors ${active ? "bg-[#0D786A] text-white" : "text-slate-600"}`}>
                <Icon size={16} /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Tally card */}
        <div className="df-card p-5" data-testid="reserve-tally">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-500">Reserved this year</span>
            <span className={`h-9 w-9 rounded-full flex items-center justify-center ${meta.ring}`}>
              <meta.icon size={18} />
            </span>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="font-num font-bold text-4xl tracking-tight text-slate-900" data-testid="reserve-reserved">
              {isLoading ? "—" : money(s?.reserved_cents)}
            </span>
            <span className="text-sm text-slate-500">of {money(s?.suggested_cents)} suggested</span>
          </div>
          {/* Progress bar */}
          <div className="mt-4 h-2.5 rounded-full bg-slate-100 overflow-hidden" data-testid="reserve-progress">
            <div className={`h-full rounded-full ${meta.bar} transition-all`} style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-2 flex items-center justify-between text-xs">
            <span className="text-slate-500">{s?.bps != null ? `${s.bps / 100}% of received` : ""}</span>
            {s?.on_track ? (
              <span className="flex items-center gap-1 text-emerald-600 font-semibold" data-testid="reserve-status">
                <CheckCircle2 size={14} /> On track
              </span>
            ) : (
              <span className="text-slate-700 font-semibold" data-testid="reserve-status">{money(s?.remaining_cents)} to go</span>
            )}
          </div>
        </div>

        <Button data-testid="reserve-add-btn" onClick={openDialog}
          className="w-full h-12 rounded-xl bg-[#0D786A] hover:bg-[#0A6055] df-pressable">
          <Plus size={18} className="mr-1.5" /> Log a {meta.label.toLowerCase()} deposit
        </Button>

        {/* Deposit list */}
        <div>
          <h2 className="text-sm font-semibold text-slate-700 mb-2">Deposits</h2>
          {(deposits || []).length ? (
            <div className="space-y-2" data-testid="reserve-deposit-list">
              {deposits.map((d) => (
                <div key={d.id} className="df-card p-4 flex items-center justify-between" data-testid={`reserve-deposit-${d.id}`}>
                  <div>
                    <div className="font-num font-semibold text-slate-900">{money(d.amount_cents)}</div>
                    <div className="text-xs text-slate-400">{fmtDateAU(d.date)}{d.note ? ` · ${d.note}` : ""}</div>
                  </div>
                  <button data-testid={`reserve-delete-${d.id}`} onClick={() => remove(d.id)}
                    className="h-9 w-9 rounded-full hover:bg-red-50 text-slate-300 hover:text-red-500 flex items-center justify-center df-pressable">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="df-card p-6 text-center">
              <p className="text-sm text-slate-500">No deposits logged yet this year.</p>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-slate-400 pt-2">Personal estimates to help you set money aside — not tax, super or financial advice.</p>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-sm">
          <DialogHeader>
            <DialogTitle>Log a {meta.label.toLowerCase()} deposit</DialogTitle>
            <DialogDescription>Record money you've actually set aside so your reserved-vs-suggested tally stays accurate.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-1">
            <div>
              <Label className="text-slate-700">Amount</Label>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-num">$</span>
                <Input type="number" step="0.01" inputMode="decimal" data-testid="reserve-amount-input" value={amount}
                  onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="h-12 rounded-xl bg-white pl-7 font-num" />
              </div>
            </div>
            <div>
              <Label className="text-slate-700">Date</Label>
              <Input type="date" data-testid="reserve-date-input" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5 h-12 rounded-xl bg-white" />
            </div>
            <div>
              <Label className="text-slate-700">Note (optional)</Label>
              <Input data-testid="reserve-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. transfer to savings" className="mt-1.5 h-12 rounded-xl bg-white" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-xl">Cancel</Button>
            <Button data-testid="reserve-save" onClick={save} disabled={saving} className="rounded-xl bg-[#0D786A] hover:bg-[#0A6055]">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
